import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Navigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './AdminPage.css';

interface CleanupResult {
  deletedUsers: number;
  deletedConversations: number;
  deletedMessages: number;
  dryRun: boolean;
}

export function AdminPage() {
  const { user, userProfile } = useAuth();
  const { t } = useLanguage();
  const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only allow flegare@gmail.com to access this page
  if (!user || user.email !== 'flegare@gmail.com') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleCleanupTestData = async (dryRun: boolean = true) => {
    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const cleanupTestData = httpsCallable(functions, 'cleanupTestData');

      const result = await cleanupTestData({ dryRun });
      setCleanupResult(result.data as CleanupResult);
    } catch (err: any) {
      setError(err.message || 'Failed to cleanup test data');
    } finally {
      setLoading(false);
    }
  };

  const handleBackfillSummaries = async () => {
    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const backfillSummaries = httpsCallable(functions, 'backfillConversationSummariesCallable');

      const result = await backfillSummaries();
      alert(`Backfill completed: ${JSON.stringify(result.data, null, 2)}`);
    } catch (err: any) {
      setError(err.message || 'Failed to backfill summaries');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adminRoot">
      <div className="adminCard">
        <div className="adminHeader">
          <h1 className="adminTitle">{t('admin.title')}</h1>
          <p className="adminSubtitle">{t('admin.subtitle')}</p>
        </div>

        <div className="adminSection">
          <h2 className="adminSectionTitle">{t('admin.testDataCleanup')}</h2>
          <p className="adminSectionDescription">
            {t('admin.testDataCleanupDesc')}
          </p>

          <div className="adminActions">
            <button
              className="adminButton adminButtonSecondary"
              onClick={() => handleCleanupTestData(true)}
              disabled={loading}
            >
              {loading ? t('common.running') : t('admin.dryRun')}
            </button>
            <button
              className="adminButton adminButtonDanger"
              onClick={() => handleCleanupTestData(false)}
              disabled={loading}
            >
              {loading ? t('common.running') : t('admin.executeCleanup')}
            </button>
          </div>

          {cleanupResult && (
            <div className="adminResult">
              <h3>{t('common.results')}:</h3>
              <pre>{JSON.stringify(cleanupResult, null, 2)}</pre>
            </div>
          )}
        </div>

        <div className="adminSection">
          <h2 className="adminSectionTitle">{t('admin.conversationSummaries')}</h2>
          <p className="adminSectionDescription">
            {t('admin.conversationSummariesDesc')}
          </p>

          <div className="adminActions">
            <button
              className="adminButton adminButtonPrimary"
              onClick={handleBackfillSummaries}
              disabled={loading}
            >
              {loading ? t('common.running') : t('admin.backfillSummaries')}
            </button>
          </div>
        </div>

        {error && (
          <div className="adminError">
            <strong>{t('common.error')}:</strong> {error}
          </div>
        )}

        <div className="adminInfo">
          <h3>{t('admin.systemInfo')}</h3>
          <p><strong>{t('admin.user')}:</strong> {userProfile?.displayName} ({user.email})</p>
          <p><strong>{t('admin.uid')}:</strong> {user.uid}</p>
          <p><strong>{t('admin.subscription')}:</strong> {userProfile?.subscriptionTier}</p>
        </div>
      </div>
    </div>
  );
}
